import { z } from 'zod';
import { overrideZoom, overrideZoomParameter, numberParameter } from './shared.js';

const detectorDimensions = { zoom: overrideZoom, grid_increment: z.number().nullable() };
const detectorSettings = z.strictObject({
  ray_settings: z.array(z.strictObject({ zoom: overrideZoom, rays_across_diameter: z.number().int() })).default([])
    .describe('Ordered NRD overrides; empty uses native 20. CODE V may round the effective sampling.'),
  detector_settings: z.array(z.discriminatedUnion('shape', [
    z.strictObject({ zoom: overrideZoom, shape: z.literal('none') }),
    z.strictObject({ ...detectorDimensions, shape: z.literal('circular'), diameter: z.number() }),
    z.strictObject({ ...detectorDimensions, shape: z.literal('rectangular'), width: z.number(), height: z.number() }),
  ])).default([]).describe('Ordered detector overrides in native image units (angular in true afocal mode). Empty retains native count-only output. Circular takes a full diameter; rectangular takes full widths. Zero diameter/width disables the detector; zero height selects a circle. Null GRI omits that command; zero GRI resets native automatic spacing. Signed values pass through unchanged. No matrix rescaling or derived throughput is added.'),
});

export default {
    label: 'Geometrical Detector', group: 'performance',
    description: 'Geometrical (ray-based) energy collected by a detector at each scan position, with ray counts.',
    settingsSchema: detectorSettings,
    parameters: [
      { key: 'ray_settings', label: 'Ray sampling', kind: 'rows', columns: [overrideZoomParameter,
        numberParameter('rays_across_diameter', 'Rays across diameter', { integer: true }),
      ] },
      { key: 'detector_settings', label: 'Detector', kind: 'rows', columns: [overrideZoomParameter,
        { key: 'shape', label: 'Shape', kind: 'select', options: [{ value: 'none', label: 'None' }, { value: 'circular', label: 'Circular' }, { value: 'rectangular', label: 'Rectangular' }] },
        numberParameter('diameter', 'Diameter', { when: { shape: ['circular'] } }),
        numberParameter('width', 'Width', { when: { shape: ['rectangular'] } }),
        numberParameter('height', 'Height', { when: { shape: ['rectangular'] } }),
        numberParameter('grid_increment', 'Grid increment', { blank: 'Unchanged', when: { shape: ['circular', 'rectangular'] } }),
      ] },
    ],
    coverage: 'At nominal focus, for every field. Detector maps are indexed by row and column; values are relative, not absolute energy. Diffraction is not included.',
  } as const;
